/**
 * The four outcomes an AGNI review of an existing DoLS authorisation can have.
 *
 * DHSC's interim guidance after *A Reference by the Attorney General for
 * Northern Ireland (Deprivation of Liberty)* [2026] UKSC 16 asks homes to
 * review every authorisation that was in force on 2 June 2026 and — the part
 * with teeth — to RECORD the decision, "particularly where authorisations are
 * left to lapse". An absence is not a decision.
 *
 * Two of these change the resident's legal position, so the confirmation copy
 * says so in plain words rather than asking "Are you sure?". Pure module — no
 * React, no store — shared by the saga (toast wording) and the page (confirm
 * dialog wording).
 *
 * `key` is posted verbatim as the `action` field of
 * POST /api/governance/liberty-review/{authorisation_uuid}/decision.
 */

export const LIBERTY_DECISION_KEEP = "keep";
export const LIBERTY_DECISION_LAPSE = "lapse";
export const LIBERTY_DECISION_CEASE = "cease";
export const LIBERTY_DECISION_DEFER_LA = "defer_la";

export const LIBERTY_DECISION_ACTIONS = [
  {
    key: LIBERTY_DECISION_KEEP,
    label: "Keep",
    /** Button tone on the row. */
    variant: "secondary",
    /** CcConfirmModal tone. */
    tone: "primary",
    confirmLabel: "Record — keep in force",
    title: "Keep this authorisation — reviewed and it stands",
    /**
     * @param {string} name resident's display name
     * @returns {string}
     */
    consequence: (name) =>
      `Reviewed against AGNI and ${name} is still deprived of their liberty. The authorisation stays in force, unchanged, and renewal chasing resumes. Nothing about ${name}'s legal position changes — you are recording that the review happened and what it concluded.`,
    toast: "Review recorded — the authorisation stays in force.",
  },
  {
    key: LIBERTY_DECISION_LAPSE,
    label: "Allow to lapse",
    variant: "dangerOutline",
    tone: "danger",
    confirmLabel: "Record — allow to lapse",
    title: "Allow this authorisation to lapse",
    consequence: (name, expiry) =>
      `This changes ${name}'s legal position. Nothing will be renewed: when the authorisation expires${
        expiry ? ` on ${expiry}` : ""
      } it simply ends, and from that date no DoLS authorisation covers ${name}. Only record this where the review concluded ${name} is not deprived of their liberty — if they are still deprived when it expires, that deprivation is unlawful.`,
    toast: "Recorded — the authorisation will be allowed to lapse.",
  },
  {
    key: LIBERTY_DECISION_CEASE,
    label: "Cease",
    variant: "dangerOutline",
    tone: "danger",
    confirmLabel: "Record — cease now",
    title: "Cease this authorisation now",
    consequence: (name) =>
      `This changes ${name}'s legal position immediately. The authorisation ends when you confirm — not on its expiry date — and from that moment nothing authorises any deprivation of ${name}'s liberty. Only record this where the review concluded ${name} is no longer deprived. Every restriction still in place must have its own lawful basis.`,
    toast: "Recorded — the authorisation has been ceased.",
  },
  {
    key: LIBERTY_DECISION_DEFER_LA,
    label: "Defer to LA",
    variant: "secondary",
    tone: "primary",
    confirmLabel: "Record — deferred to the LA",
    title: "Defer to the local authority",
    consequence: (name) =>
      `The local authority has asked you to wait. The authorisation stays in force and ${name}'s legal position does not change; the review parks here with the deferral recorded against it, and renewal chasing stays suppressed until the LA responds.`,
    toast: "Recorded — the review is deferred to the local authority.",
  },
];

export const libertyDecisionAction = (key) =>
  LIBERTY_DECISION_ACTIONS.find((item) => item.key === key) || null;

/** Toast wording for a completed decision. Falls back, never blank. */
export const libertyDecisionToast = (key) =>
  libertyDecisionAction(key)?.toast || "Review decision recorded.";
